/**
 * Command Briefing types and pure helpers (plan 03 CHG-13). The payload shape
 * mirrors `GET /api/dashboard/briefing` (app/schemas/dashboard.py
 * BriefingResponse). No path aliases, so `npm run test:unit` runs this file
 * with Node's type stripping.
 */

export interface BriefingLead {
  candidate_id: string
  driver_name: string
  role_label: string | null
  license_state: string | null
  created_at: string | null
  href: string
}

export type BriefingItemKind = 'needs_decision' | 'waiting_on_driver'

export interface BriefingItem {
  candidate_id: string
  driver_name: string
  kind: BriefingItemKind | string
  reason: string
  policy_code: string | null
  waiting_since: string | null
  waiting_minutes: number | null
  recruiter_name: string | null
  href: string
}

export interface BriefingCoworker {
  key: string
  name: string
  photo: string
  actions_today: number
  last_action: string | null
  active: boolean
}

export interface Briefing {
  generated_at: string
  assistant: { name: string; photo: string }
  headline: string
  counts: { new_leads: number; needs_decision: number; waiting_on_driver: number; screenings_today: number }
  new_leads: BriefingLead[]
  top_items: BriefingItem[]
  coworkers_today: BriefingCoworker[]
  suggested_prompts: string[]
  poll_seconds: number
}

/** "Good morning" before noon, "Good afternoon" before 5 pm, else "Good evening", by the browser clock. */
export function greetingFor(now: Date): string {
  const hour = now.getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

/** "SUNDAY 4 OCTOBER", the hero's date line. */
export function dateLine(now: Date): string {
  const weekday = now.toLocaleDateString('en-GB', { weekday: 'long' })
  const day = now.getDate()
  const month = now.toLocaleDateString('en-GB', { month: 'long' })
  return `${weekday} ${day} ${month}`.toUpperCase()
}

/** "18 min", "2 h", "3 d": compact, readable from the back of a room. */
export function formatMinutes(minutes: number | null | undefined): string | null {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return null
  const value = Math.max(0, Math.round(minutes))
  if (value < 60) return `${value} min`
  if (value < 60 * 24) return `${Math.round(value / 60)} h`
  return `${Math.round(value / (60 * 24))} d`
}

/** Minutes between an ISO timestamp and now, or null for a missing or bad value. */
export function minutesSince(iso: string | null | undefined, now: Date): number | null {
  if (!iso) return null
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return null
  return Math.max(0, (now.getTime() - then) / 60000)
}

/**
 * Prompt box copy (Plan 10 item 4). The box speaks for the whole team of AI
 * Employees, not one named persona, so no name and no pronoun appear here; the
 * placeholder and the screen-reader label say the same thing.
 */
export const ASK_BAR_PROMPT = 'Ask your AI Employees anything, or tell them what to do'
export const ASK_BAR_SEND_LABEL = 'Send'
export const ASK_BAR_THINKING_LABEL = 'Thinking'

export type AskIntent = 'approve' | 'open' | 'ask'

/**
 * What a typed line means, as UI routing only (plan 03 CHG-13; PA AskBar's
 * reader without its approve action). "Approve this" never approves: like
 * "open it", it navigates to the item that needs a decision, where the
 * Workbench's own confirm dialog records the decision. A question about
 * approving is a question ("Should I approve Angela?"), and so is anything
 * that names a rejection.
 */
export function readIntent(raw: string): AskIntent {
  const trimmed = raw.toLowerCase().trim()
  const text = ` ${trimmed} `
  const has = (...words: string[]) => words.some((w) => new RegExp(`\\b${w}\\b`).test(text))
  const asking =
    trimmed.endsWith('?') ||
    /^(what|why|how|when|which|who|is|are|was|were|do|does|did|can|could|should|would|will|tell me)\b/.test(trimmed)

  if (has('disapprove', 'reject', 'decline')) return 'ask'
  if (!asking && has('approve', 'approved', 'accept', 'go ahead', 'sign off')) return 'approve'
  if (!asking && has('open', 'show', 'take me', 'go to', 'pull up') && has('it', 'this', 'record', 'case', 'driver', 'her', 'him')) {
    return 'open'
  }
  return 'ask'
}

/** A row of the header bell (components/NotificationCenter), built from the briefing. */
export interface BriefingNotification {
  id: string
  type: 'info' | 'warning'
  title: string
  message: string
  timestamp: Date
  read: boolean
  href: string
}

/**
 * The bell shows what the briefing says needs the recruiter (plan 03 CHG-13):
 * decision items first as warnings, then waiting drivers and new leads. Ids are
 * stable per driver and kind, so a poll does not mark a read row unread.
 */
export function briefingNotifications(briefing: Briefing | null, now: Date): BriefingNotification[] {
  if (!briefing) return []
  const stamp = (iso: string | null) => (iso && !Number.isNaN(Date.parse(iso)) ? new Date(iso) : now)
  const items = briefing.top_items.map((item) => ({
    id: `${item.kind}:${item.candidate_id}`,
    type: item.kind === 'needs_decision' ? ('warning' as const) : ('info' as const),
    title: item.driver_name,
    message: item.policy_code ? `${item.reason} (${item.policy_code})` : item.reason,
    timestamp: stamp(item.waiting_since),
    read: false,
    href: item.href,
  }))
  const leads = briefing.new_leads.map((lead) => ({
    id: `lead:${lead.candidate_id}`,
    type: 'info' as const,
    title: `New lead: ${lead.driver_name}`,
    message: [lead.role_label, lead.license_state].filter(Boolean).join(', ') || 'Applied today',
    timestamp: stamp(lead.created_at),
    read: false,
    href: lead.href,
  }))
  return [...items, ...leads]
}
